import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

function ShieldLogo({ className = "h-9 w-9" }) {
    return (
        <svg
            viewBox="0 0 40 44"
            className={className}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="shieldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5A4" />
                    <stop offset="100%" stopColor="#0F766E" />
                </linearGradient>
                <linearGradient id="shieldStroke" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" />
                    <stop offset="100%" stopColor="#B8941F" />
                </linearGradient>
            </defs>
            <path
                d="M20 2 L36 8 V20 C36 30 28 38 20 42 C12 38 4 30 4 20 V8 Z"
                fill="url(#shieldGrad)"
                stroke="url(#shieldStroke)"
                strokeWidth="1.4"
            />
            <path
                d="M11 18 Q15 14 20 18 T29 18 M11 23 Q15 19 20 23 T29 23 M11 28 Q15 24 20 28 T29 28"
                stroke="#D4AF37"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
            />
        </svg>
    );
}

const NAV_ITEMS = [
    { label: "Features", to: "/#features" },
    {
        label: "Use Cases",
        to: "/#use-cases",
        dropdown: [
            { label: "Government Contractors", to: "/#government" },
            { label: "Private Bidders", to: "/#private" },
            { label: "MSMEs & Startups", to: "/#private" },
        ],
    },
    { label: "Pricing", to: "/pricing" },
    {
        label: "Resources",
        to: "/faq",
        dropdown: [
            { label: "FAQ", to: "/faq" },
            { label: "Documentation", to: "/faq" },
            { label: "Help Center", to: "/contact" },
            { label: "Blog", to: "/contact" },
        ],
    },
    {
        label: "Company",
        to: "/about",
        dropdown: [
            { label: "About Us", to: "/about" },
            { label: "Contact", to: "/contact" },
            { label: "Privacy Policy", to: "/privacy" },
            { label: "Refund Policy", to: "/refund-policy" },
            { label: "Terms", to: "/terms" },
        ],
    },
];

function DesktopNav({ activeDrop, setActiveDrop }) {
    return (
        <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
                <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => item.dropdown && setActiveDrop(item.label)}
                    onMouseLeave={() => setActiveDrop(null)}
                >
                    <Link
                        to={item.to}
                        className="flex items-center gap-1 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-300 hover:text-white transition-colors"
                        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                    >
                        {item.label}
                        {item.dropdown && <ChevronDown className="h-3 w-3 opacity-60" />}
                    </Link>
                    {item.dropdown && activeDrop === item.label && (
                        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-56">
                            <div className="glass-card rounded-2xl p-2">
                                {item.dropdown.map((d) => (
                                    <Link
                                        key={d.label}
                                        to={d.to}
                                        className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                                    >
                                        {d.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </nav>
    );
}

function MobileMenu({ open, setOpen }) {
    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
                <button
                    className="ml-1 h-10 w-10 flex items-center justify-center rounded-full border border-white/15 text-white"
                    data-testid="mobile-menu-btn"
                    aria-label="Open menu"
                >
                    <Menu className="h-5 w-5" />
                </button>
            </SheetTrigger>
            <SheetContent
                side="right"
                className="w-[320px] bg-[#071A2F] border-l border-white/10"
            >
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="flex items-center gap-2 mb-8">
                    <ShieldLogo className="h-8 w-8" />
                    <span className="font-bold text-base text-white">
                        TenderTrack<span className="gradient-text-teal">AI</span>
                    </span>
                </div>
                <nav className="flex flex-col gap-1">
                    {NAV_ITEMS.map((i) => (
                        <Link
                            key={i.label}
                            to={i.to}
                            onClick={() => setOpen(false)}
                            className="px-3 py-3 text-sm font-semibold uppercase tracking-wider text-slate-300 hover:text-white hover:bg-white/5 rounded-lg"
                        >
                            {i.label}
                        </Link>
                    ))}
                    <div className="h-px bg-white/10 my-4" />
                    <Link to="/login" onClick={() => setOpen(false)}>
                        <Button
                            variant="outline"
                            className="w-full border-white/15 bg-transparent text-white hover:bg-white/5"
                        >
                            Log In
                        </Button>
                    </Link>
                    <Link to="/signup" onClick={() => setOpen(false)}>
                        <button className="luxury-btn-gold mt-2 w-full px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]">
                            Sign Up Free
                        </button>
                    </Link>
                </nav>
            </SheetContent>
        </Sheet>
    );
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeDrop, setActiveDrop] = useState(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={cn(
                "fixed top-0 inset-x-0 z-50 transition-all duration-300",
                scrolled ? "glass-nav" : "bg-transparent",
            )}
            data-testid="main-navbar"
        >
            <div className="container-tight flex h-20 items-center justify-between">
                <Link
                    to="/"
                    className="flex items-center gap-2.5 group"
                    data-testid="navbar-logo"
                >
                    <ShieldLogo />
                    <span className="font-bold text-lg tracking-tight text-white">
                        TenderTrack
                        <span className="gradient-text-teal">AI</span>
                    </span>
                </Link>

                <DesktopNav activeDrop={activeDrop} setActiveDrop={setActiveDrop} />

                <div className="flex items-center gap-2">
                    <Link
                        to="/login"
                        className="hidden sm:block"
                        data-testid="navbar-login-btn"
                    >
                        <button className="px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white border border-white/15 rounded-full hover:bg-white/5 transition-colors">
                            Log In
                        </button>
                    </Link>
                    <Link to="/signup" data-testid="navbar-signup-btn">
                        <button className="luxury-btn-gold px-4 lg:px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]">
                            Sign Up Free
                        </button>
                    </Link>

                    <MobileMenu open={open} setOpen={setOpen} />
                </div>
            </div>
        </header>
    );
}
